import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useCaps } from '../../contexts/CapabilitiesContext';

export interface AdminSubtabConfig {
  id: number;
  tab: number;
  tab_key: string;
  key: string;
  label: string;
  display_order: number;
  is_visible: boolean;
}

export interface DeclaredSubtab {
  key: string;
  label: string;
  cap: string;
}

interface AdminSubtabsContextValue {
  subtabs: AdminSubtabConfig[];
  refresh: () => Promise<void>;
}

const AdminSubtabsContext = createContext<AdminSubtabsContextValue>({
  subtabs: [],
  refresh: async () => {},
});

export const AdminSubtabsProvider: React.FC<{
  subtabs: AdminSubtabConfig[];
  refresh: () => Promise<void>;
  children: React.ReactNode;
}> = ({ subtabs, refresh, children }) => (
  <AdminSubtabsContext.Provider value={{ subtabs, refresh }}>
    {children}
  </AdminSubtabsContext.Provider>
);

export function useAdminSubtabs() {
  return useContext(AdminSubtabsContext);
}

/**
 * Merge code-declared SUBTABS with DB rows:
 * - drop DB-hidden rows
 * - override label from DB when present
 * - sort by display_order
 * - append any code-only entry that has no DB row (so not-yet-seeded subtabs still render)
 * - filter by capability
 */
export function useOrderedSubtabs<T extends DeclaredSubtab>(
  tabKey: string,
  declared: T[]
): T[] {
  const { can } = useCaps();
  const { subtabs } = useAdminSubtabs();

  return useMemo(() => {
    const dbRows = subtabs
      .filter((row) => row.tab_key === tabKey)
      .slice()
      .sort((a, b) => a.display_order - b.display_order || a.id - b.id);

    const byKey = new Map(dbRows.map((row) => [row.key, row]));
    const result: T[] = [];

    for (const row of dbRows) {
      if (!row.is_visible) continue;
      const code = declared.find((d) => d.key === row.key);
      if (!code) continue;
      if (!can(code.cap)) continue;
      result.push({ ...code, label: row.label || code.label });
    }

    for (const code of declared) {
      if (byKey.has(code.key)) continue;
      if (!can(code.cap)) continue;
      result.push(code);
    }

    return result;
  }, [tabKey, declared, subtabs, can]);
}

export function useRefreshSubtabs() {
  const { refresh } = useAdminSubtabs();
  return useCallback(() => refresh(), [refresh]);
}
